import { BaseTokenModule } from './base'
import { BigNumber, ethers } from 'ethers'
import { TokenBalance } from '../../../../../../graphql/generated/graphql'
import { useWeb3Connection } from '../../../../../../web3/Web3Provider'
import { useAllowanceContext } from '../../allowance/AllowanceProvider'
import { useWalletAction } from '../useWalletAction'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CreateDirectPayFormData } from '../../usePayBill'
import { useAmountWithFee } from '../useAmountWithFee'
import { isAcceptedPaymentToken } from '@spritz-finance/sdk'
import { FadePresence } from '../../../../../../components/FadePresence'
import { ButtonsWrapper } from '../ButtonsWrapper'
import { Button, Spacer } from '@spritz-finance/ui'
import { AllowanceInfo } from '../AllowanceInfo'
import { TokenImage } from '../../TokenListItem'
import { TransactionLogs } from '../../TransactionLogs'
import { AllowanceConfiguration } from '../AllowanceConfiguration'

const fiatValueToTokenAmount = (num: string | number | undefined, decimals: number) => {
    if (!num || `${num}`.length === 0) return BigNumber.from(0)
    const formattedAmount = parseFloat(`${num}`).toFixed(2)
    const convertedDecimals = Math.round(parseFloat(formattedAmount) * 100)
    return ethers.utils.parseUnits(convertedDecimals.toString(), decimals - 2)
}

const TokenWithAllowance = ({
    token,
    submitting,
}: {
    token: TokenBalance
    submitting: boolean
}) => {
    const { connectedNetwork } = useWeb3Connection()
    const {
        allowanceLoading,
        currentAllowance,
        hasAllowance,
        approve,
        approvalLoading,
        actionLog,
        clearActionLog,
    } = useAllowanceContext()
    const action = useWalletAction()
    const [shouldPersistApprovalButton, setShouldPersistApprovalButton] = useState(false)
    const { watch } = useFormContext<CreateDirectPayFormData>()
    const amount = watch('amount')
    const totalAmount = useAmountWithFee(amount?.toString())

    const validateFiatValue = isAcceptedPaymentToken(token.address, connectedNetwork!)

    const tokenAmount = fiatValueToTokenAmount(totalAmount, token.decimals)

    const handleTokenApproval = () => {
        setShouldPersistApprovalButton(true)
        approve()
    }

    let requiresAllowance

    if (!validateFiatValue) {
        requiresAllowance = currentAllowance?.isZero() ?? false
    } else {
        requiresAllowance = validateFiatValue
            ? !hasAllowance || currentAllowance?.lt(tokenAmount)
            : !approvalLoading && hasAllowance
    }

    const showApprovalButton =
        (!approvalLoading && requiresAllowance) || shouldPersistApprovalButton

    const actionText = useWalletAction()

    return (
        <FadePresence renderKey={currentAllowance?.toString()} style={{ width: '100%' }}>
            <ButtonsWrapper>
                {allowanceLoading ? (
                    <Button.CTA loading={true} />
                ) : (
                    <>
                        {showApprovalButton && (
                            <>
                                <AllowanceInfo token={token} />
                                <Spacer height={16} />
                                <Button.CTA
                                    disabled={!requiresAllowance}
                                    onClick={handleTokenApproval}
                                    loading={approvalLoading}
                                >
                                    {approvalLoading
                                        ? 'Approving'
                                        : `Allow Spritz to ${actionText} your`}
                                    <Spacer width={8} />
                                    <TokenImage src={token?.tokenImageUrl} />
                                </Button.CTA>
                                <Spacer height={16} />
                            </>
                        )}

                        <Button.CTA
                            variant="gradient"
                            disabled={requiresAllowance}
                            type="submit"
                            loading={submitting}
                            onClick={clearActionLog}
                        >
                            {action} <Spacer width={8} />
                            <TokenImage src={token.tokenImageUrl} />
                        </Button.CTA>

                        {showApprovalButton && (
                            <>
                                {actionLog && <Spacer height={16} />}
                                <TransactionLogs log={actionLog} />
                            </>
                        )}

                        <AllowanceConfiguration />
                    </>
                )}
            </ButtonsWrapper>
        </FadePresence>
    )
}

export class ERC20TokenModule extends BaseTokenModule {
    allowanceTarget(): string {
        return this.token.address ?? '0000000'
    }

    paymentRequiresAllowance() {
        return true
    }

    content(args: any): JSX.Element | null {
        return <TokenWithAllowance token={this.token} submitting={args.submitting} />
    }
}
